import type { CaptureResult } from "posthog-js";
import { PDF_TOOLS } from "./pdf-tools";

export const WEBSITE_MEASUREMENT_RELEASE = "website_journey_v1";
const ROUTES = new Set(["/", "/get-started", "/pricing", "/login", "/signup", "/dashboard", "/dashboard/billing", "/dashboard/settings", "/dashboard/survey", "/connect", ...Object.keys(PDF_TOOLS).map(key => `/${key}`)]);
export function measurementRoute(path: string): string {
  return ROUTES.has(path) ? path : "/other";
}

/** Question: which coarse acquisition channel reaches signup and useful work? */
export function acquisitionChannel(search: string, referrer: string): string {
  const source = new URLSearchParams(search).get("utm_source")?.toLowerCase();
  if (source && ["google", "bing", "chatgpt", "claude", "facebook", "linkedin", "twitter", "x", "youtube"].includes(source)) return source;
  if (source) return "campaign_other";
  try {
    const host = new URL(referrer).hostname;
    if (host === "fillapp.ai" || host.endsWith(".fillapp.ai")) return "internal";
    for (const domain of ["google.com", "bing.com", "chatgpt.com", "claude.ai", "facebook.com", "linkedin.com", "youtube.com", "t.co"]) {
      if (host === domain || host.endsWith(`.${domain}`)) return domain.split(".")[0];
    }
    return "referral_other";
  } catch { return "direct_or_unknown"; }
}

const SAFE_PROPERTIES = new Set([
  "distinct_id", "$session_id", "$window_id", "$lib", "$lib_version", "$insert_id", "$process_person_profile", "$is_identified", "$anon_distinct_id", "$device_id", "$user_id", "token",
  "flow", "entry", "attempt_id", "export_id", "source", "input_format", "format", "committed_edit_observed", "schema_version", "release", "outcome", "reason", "stage", "duration_ms", "export_index", "retry_after_failure", "feedback_step", "failure_reason", "edit_category", "actor", "import_id",
  "surface", "client", "client_release", "event_source", "observation", "measurement_version", "acquisition_channel", "landing_route", "route", "authProvider", "redirectedFromExtension", "isNewUser",
  "planId", "planName", "billingPeriod", "price", "lookupKey", "fromPlan", "toPlan", "usdAmount", "credits", "creditsRemaining", "userType", "trialEndsAt", "error_code", "errorType", "hasData", "autoClose", "location", "variant", "demo", "test_name", "synthetic", "test_run_id",
]);
const ENUM_PROPERTIES: Record<string, ReadonlySet<string>> = {
  source: new Set(["upload", "sample", "restored", "sonner", "custom_toast", "billing_plan_card", "dashboard_plan_card"]),
  reason: new Set(["none", "edit_not_saved", "export_failed", "document_load_failed", "not_provided", "missing_or_wrong_text", "layout", "download_problem", "other", "storage_failed", "invalid_or_unsupported_input"]),
  location: new Set(["chat_header"]),
  errorType: new Set(["FillAppError", "string", "object", "undefined", "number", "boolean"]),
};
const ERROR_TYPES = new Set(["bad_request", "unauthorized", "forbidden", "not_found", "conflict", "rate_limit", "quota", "internal_server", "offline"]);
const ERROR_SURFACES = new Set(["chat", "auth", "api", "stream", "database", "history", "vote", "document", "suggestions", "user", "admin", "email", "snippets", "subscription", "webhooks", "models", "apps", "topup"]);
const BILLING_REASONS = new Set(["subscription_inactive", "free_quota_exceeded", "credit_limit_reached", "subscription_required", "insufficient_credits"]);
function safeValue(key: string, value: unknown): unknown {
  if (key === "error_code") {
    if (typeof value !== "string") return "unknown";
    const parts = value.split(":");
    return parts.length === 2 && ((ERROR_TYPES.has(parts[0]) && ERROR_SURFACES.has(parts[1])) ||
      (parts[0] === "billing" && BILLING_REASONS.has(parts[1]))) ? value : "unknown";
  }
  if (ENUM_PROPERTIES[key]) return typeof value === "string" && ENUM_PROPERTIES[key].has(value) ? value : "unknown";
  return value;
}
/** Every website event crosses this boundary. No inherited URL/content/profile payloads. */
export function sanitizeWebsiteMeasurement(event: CaptureResult | null): CaptureResult | null {
  if (!event) return event;
  const safe = { ...event };
  delete safe.$set;
  delete safe.$set_once;
  const requestedSurface = event.properties?.surface;
  const surface = ["website", "pdf_editor", "pdf_assistant"].includes(requestedSurface)
    ? requestedSurface
    : event.event.startsWith("pdf_") ? "pdf_editor"
    : /^(chat_|message_|toast_|credit|suggested_action|new_chat_)/.test(event.event) ? "pdf_assistant" : "website";
  return { ...safe, properties: {
    ...Object.fromEntries(Object.entries(event.properties || {}).filter(([key, value]) =>
      SAFE_PROPERTIES.has(key) && (value === null || ["string", "number", "boolean"].includes(typeof value)))
      .map(([key, value]) => [key, safeValue(key, value)])),
    surface,
    client: "website", event_source: "website", observation: "client",
    measurement_version: WEBSITE_MEASUREMENT_RELEASE,
    client_release: process.env.NEXT_PUBLIC_SOURCE_VERSION || "unknown",
  } };
}

interface IdentityClient {
  get_property(key: string): unknown;
  identify(id: string, properties?: Record<string, unknown>): void;
  reset(): void;
  capture(name: string): unknown;
}
/** Question: does the same account activate, without joining distinct signed-in people? */
export function syncWebsiteIdentity(client: IdentityClient, uid: string | null): void {
  try {
    const previous = client.get_property("$user_id");
    if (previous && previous !== uid) {
      if (!uid) client.capture("user_signout");
      client.reset();
    }
    if (uid && previous !== uid) client.identify(uid, { authProvider: "google" });
  } catch { /* Analytics must never change auth state. */ }
}

/** Clear a stale account before restoration; do not identify an unverified new user. */
export function clearPreviousWebsiteIdentity(client: IdentityClient, uid: string | null): void {
  try {
    const previous = client.get_property("$user_id");
    if (previous && previous !== uid) syncWebsiteIdentity(client, null);
  } catch { /* Analytics must never change auth state. */ }
}
